import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Bot,
  CircleCheck,
  CircleDashed,
  KeyRound,
  MessageSquareWarning,
} from 'lucide-react'
import { useState, type ReactNode } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

import type { WorkingHours } from '@aeris/contracts'
import {
  Badge,
  Button,
  Combobox,
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  EmptyState,
  Field,
  HelpHint,
  Input,
  MoneyInput,
  OrderedChoiceList,
  Section,
  Spinner,
  SwitchField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeaderCell,
  TableRow,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  TagInput,
  Textarea,
  type ComboboxItem,
  type OrderedChoiceItem,
} from '@aeris/ui'

import { TabHeader } from '../components/config-tabs.tsx'
import { WorkingHoursEditor } from '../components/working-hours-editor.tsx'
import {
  ApiError,
  fetchBotConfigs,
  fetchBotSessions,
  fetchLlmCredentials,
  saveBotConfig,
  saveLlmCredential,
  type BotConfigSummaryType,
  type LlmCredentialSummaryType,
} from '../lib/api.ts'
import { useAuth } from '../lib/auth-context.ts'

/**
 * Atendimento → Robô: a configuração do robô de IA, os provedores e o gasto
 * do período (F052, sem JSON algum desde a F058).
 *
 * A F047 já deu a cada fila um `botFirst` — a chave que decide se o robô
 * fala antes da fila humana DENTRO do horário. Esta tela é o robô em si:
 * ligar/desligar, a ordem de provedores (com fallback só em 429/5xx/529,
 * nunca em chave errada — ADR-0032), o prompt, o horário HUMANO (fora dele
 * o robô sempre responde), as palavras de handoff e os tetos — um limite
 * DURO de custo por SESSÃO do robô (não pela vida inteira da conversa — ver
 * o comentário na migração 0037), porque cada chamada é dinheiro de verdade.
 *
 * Uma config só, a do TENANT INTEIRO (`queueId: null`) — a sobrescrita por
 * fila que a API já aceita não tem formulário aqui ainda, ver "O que ficou
 * de fora" em docs/features/F052-bot.md.
 */

const PROVIDERS = [
  'anthropic',
  'openai',
  'gemini',
  'deepseek',
  'groq',
  'openrouter',
] as const

const PROVIDER_LABELS: Record<string, string> = {
  anthropic: 'Anthropic (Claude)',
  openai: 'OpenAI (ChatGPT)',
  gemini: 'Gemini',
  deepseek: 'DeepSeek',
  groq: 'Groq',
  openrouter: 'OpenRouter',
}

const END_REASON_LABELS: Record<string, string> = {
  handoff_keyword: 'Cliente pediu atendente',
  handoff_model: 'O robô decidiu encaminhar',
  max_replies: 'Limite de respostas',
  cost_cap: 'Teto de custo',
  window_closed: 'Janela de 24h fechou',
  human_took_over: 'Humano assumiu',
  error: 'Erro',
  conversation_merged: 'Conversa unida a outra do mesmo cliente',
}

/**
 * Modelos conhecidos por provedor, do catálogo em `packages/llm/src/
 * pricing.ts` (conferido em 15/08/2026) — a "opção Outro" cobre um modelo
 * novo que a tabela ainda não lista. OpenRouter fica de fora: não tem
 * catálogo fixo (repassa o preço do provedor upstream, escolhido livremente
 * dentro do OpenRouter — ver o comentário em `pricingFor`).
 */
const MODEL_OPTIONS: Partial<
  Record<
    (typeof PROVIDERS)[number],
    readonly { value: string; label: string }[]
  >
> = {
  anthropic: [
    { value: 'claude-opus-5', label: 'Claude Opus 5' },
    { value: 'claude-sonnet-5', label: 'Claude Sonnet 5' },
  ],
  openai: [
    { value: 'gpt-5.6-terra', label: 'GPT-5.6 Terra' },
    { value: 'gpt-5.6-luna', label: 'GPT-5.6 Luna' },
  ],
  gemini: [
    { value: 'gemini-2.5-flash', label: 'Gemini 2.5 Flash' },
    { value: 'gemini-2.5-flash-lite', label: 'Gemini 2.5 Flash Lite' },
  ],
  deepseek: [
    { value: 'deepseek-v4-flash', label: 'DeepSeek V4 Flash' },
    { value: 'deepseek-v4-pro', label: 'DeepSeek V4 Pro' },
  ],
  /*
   * Só os dois GPT OSS: são os que a Groq serve com preço público e 131k de
   * contexto. Os Llama, que seriam o padrão óbvio, saíram do catálogo público
   * dela em 26/08/2026 e hoje respondem "Contact Sales" — listá-los daria à
   * loja uma opção que falha na primeira chamada. "Outro" cobre quem tiver
   * contrato.
   */
  groq: [
    { value: 'openai/gpt-oss-120b', label: 'GPT OSS 120B' },
    { value: 'openai/gpt-oss-20b', label: 'GPT OSS 20B' },
  ],
}

const OTHER_MODEL_VALUE = '__other__'

const DEFAULT_WORKING_HOURS: WorkingHours = {
  timezone: 'America/Sao_Paulo',
  windows: [],
}

const tenantConfig = (
  configs: readonly BotConfigSummaryType[]
): BotConfigSummaryType | undefined => configs.find((c) => c.queueId === null)

/** Uma linha "provedor → modelo": Combobox de modelos conhecidos, ou um Input livre quando não há catálogo (OpenRouter) ou a pessoa escolheu "Outro". */
const ModelByProviderRow = ({
  provider,
  value,
  isOther,
  onSelectKnown,
  onSelectOther,
  onCustomTextChange,
  disabled,
}: {
  provider: (typeof PROVIDERS)[number]
  value: string
  isOther: boolean
  onSelectKnown: (modelId: string) => void
  onSelectOther: () => void
  onCustomTextChange: (text: string) => void
  disabled: boolean
}): ReactNode => {
  const known = MODEL_OPTIONS[provider]

  if (known === undefined) {
    return (
      <Field
        label={PROVIDER_LABELS[provider] ?? provider}
        hint="OpenRouter não tem catálogo fixo — digite o id do modelo upstream. Vazio usa o padrão do provedor."
      >
        {(field) => (
          <Input
            {...field}
            disabled={disabled}
            value={value}
            onChange={(event) => {
              onCustomTextChange(event.target.value)
            }}
            placeholder="ex.: meta-llama/llama-3.3-70b-instruct"
          />
        )}
      </Field>
    )
  }

  const items: ComboboxItem[] = [
    ...known,
    { value: OTHER_MODEL_VALUE, label: 'Outro (digite o id do modelo)' },
  ]
  const comboboxValue =
    value === '' && !isOther ? null : isOther ? OTHER_MODEL_VALUE : value
  const selectedLabel =
    comboboxValue === null
      ? null
      : comboboxValue === OTHER_MODEL_VALUE
        ? 'Outro (digite o id do modelo)'
        : (known.find((option) => option.value === value)?.label ?? value)

  return (
    <div className="flex flex-col gap-2">
      <Field
        label={PROVIDER_LABELS[provider] ?? provider}
        hint="Vazio usa o modelo padrão deste provedor."
      >
        {(field) => (
          <Combobox
            {...field}
            disabled={disabled}
            value={comboboxValue}
            onValueChange={(next) => {
              if (next === null) {
                onSelectKnown('')
                return
              }
              if (next === OTHER_MODEL_VALUE) {
                onSelectOther()
                return
              }
              onSelectKnown(next)
            }}
            items={items}
            search=""
            onSearchChange={() => {
              // Lista fixa e curta — não há busca a fazer.
            }}
            selectedLabel={selectedLabel}
            clearable
            placeholder="Modelo padrão"
          />
        )}
      </Field>
      {isOther ? (
        <Input
          disabled={disabled}
          value={value}
          aria-label={`${PROVIDER_LABELS[provider]} — id do modelo (outro)`}
          onChange={(event) => {
            onCustomTextChange(event.target.value)
          }}
          placeholder="id do modelo"
        />
      ) : null}
    </div>
  )
}

const ConfigForm = ({
  config,
}: {
  config: BotConfigSummaryType | undefined
}): ReactNode => {
  const queryClient = useQueryClient()
  const [formError, setFormError] = useState<string | null>(null)

  // Campos que não são `<input>`s nativos vivem FORA do react-hook-form —
  // `watch()` quebraria o React Compiler (CLAUDE.md), e `Controller` para
  // cada um destes seria mais código que só espelhar em `useState`, o mesmo
  // caminho que `party-form.tsx` já usa para o campo `kind`.
  const [enabled, setEnabled] = useState(config?.enabled ?? false)
  const [providerOrder, setProviderOrder] = useState<
    (typeof PROVIDERS)[number][]
  >(() => {
    const configured = config?.providerOrder ?? []
    const rest = PROVIDERS.filter((p) => !configured.includes(p))
    return [...configured, ...rest]
  })
  const [modelByProvider, setModelByProvider] = useState<
    Record<string, string>
  >(() => ({ ...(config?.modelByProvider ?? {}) }))
  const [modelOther, setModelOther] = useState<Set<string>>(() => {
    const initial = new Set<string>()
    for (const provider of PROVIDERS) {
      const known = MODEL_OPTIONS[provider]
      const value = config?.modelByProvider[provider]
      if (
        known !== undefined &&
        value !== undefined &&
        value !== '' &&
        !known.some((option) => option.value === value)
      ) {
        initial.add(provider)
      }
    }
    return initial
  })
  const [workingHours, setWorkingHours] = useState<WorkingHours>(
    config?.workingHours ?? DEFAULT_WORKING_HOURS
  )
  const [handoffKeywords, setHandoffKeywords] = useState<string[]>(
    config?.handoffKeywords ?? ['atendente', 'humano', 'pessoa']
  )

  const configFormSchema = z.object({
    systemPrompt: z.string().trim().max(8000),
    greeting: z.string().trim().max(2000),
    maxRepliesPerSession: z.number().int().min(1).max(1000),
  })
  type ConfigFormValues = z.infer<typeof configFormSchema>

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ConfigFormValues>({
    resolver: zodResolver(configFormSchema),
    defaultValues: {
      systemPrompt: config?.systemPrompt ?? '',
      greeting: config?.greeting ?? '',
      maxRepliesPerSession: config?.maxRepliesPerSession ?? 5,
    },
  })

  const [maxCostWire, setMaxCostWire] = useState(
    config
      ? (Number(config.maxCostMicrosUsdPerSession) / 1_000_000).toFixed(2)
      : '0.50'
  )
  const maxCostInvalid = Number(maxCostWire || '0') <= 0

  const save = useMutation({
    mutationFn: async (values: ConfigFormValues) => {
      const maxCostMicrosUsdPerSession = String(
        Math.round(Number(maxCostWire || '0') * 1_000_000)
      )

      await saveBotConfig({
        queueId: null,
        enabled,
        providerOrder,
        modelByProvider: Object.fromEntries(
          Object.entries(modelByProvider).filter(([, v]) => v.trim() !== '')
        ),
        systemPrompt: values.systemPrompt,
        greeting: values.greeting === '' ? null : values.greeting,
        workingHours,
        handoffKeywords,
        maxRepliesPerSession: values.maxRepliesPerSession,
        maxCostMicrosUsdPerSession,
        version: config?.version,
      })
    },
    onSuccess: async () => {
      setFormError(null)
      await queryClient.invalidateQueries({ queryKey: ['bot-configs'] })
    },
    onError: (cause: unknown) => {
      setFormError(
        cause instanceof ApiError
          ? cause.message
          : cause instanceof Error
            ? cause.message
            : 'Não foi possível salvar a configuração.'
      )
    },
  })

  const onSubmit = handleSubmit((values) => {
    setFormError(null)
    save.mutate(values)
  })

  return (
    <form
      noValidate
      className="flex flex-col gap-5"
      onSubmit={(event) => {
        void onSubmit(event)
      }}
    >
      <Section title="Robô">
        <SwitchField
          label="Robô ligado"
          description="Enquanto desligado, toda conversa segue direto para a fila humana."
          checked={enabled}
          onCheckedChange={setEnabled}
        />

        <Field label="Saudação" error={errors.greeting?.message}>
          {(field) => <Input {...field} {...register('greeting')} />}
        </Field>

        <Field
          label="Prompt do sistema"
          error={errors.systemPrompt?.message}
          hint="O que o robô sabe sobre a loja, e como deve responder."
        >
          {(field) => (
            <Textarea {...field} {...register('systemPrompt')} rows={5} />
          )}
        </Field>
      </Section>

      <Section
        title="Ordem dos provedores"
        description="A ordem de tentativa — cai para o próximo só em erro do provedor (429/5xx), nunca por chave errada. Um provedor sem chave cadastrada (aba Provedores) é pulado automaticamente."
        action={
          <HelpHint label="ordem dos provedores">
            O robô tenta o primeiro; só cai para o próximo em 429 (limite de
            uso) ou 5xx/529 (o provedor caiu) — nunca por chave errada (401/403)
            ou pedido inválido (400), porque trocar de provedor não conserta uma
            chave errada.
          </HelpHint>
        }
      >
        <OrderedChoiceList
          items={providerOrder.map((provider) => ({
            value: provider,
            label: PROVIDER_LABELS[provider],
          }))}
          onReorder={(items: OrderedChoiceItem[]) => {
            setProviderOrder(
              items.map((item) => item.value as (typeof PROVIDERS)[number])
            )
          }}
        />
      </Section>

      <Section
        title="Modelo por provedor"
        description="Um modelo por provedor — vazio usa o padrão de cada um."
      >
        <div className="flex flex-col gap-4">
          {PROVIDERS.map((provider) => (
            <ModelByProviderRow
              key={provider}
              provider={provider}
              value={modelByProvider[provider] ?? ''}
              isOther={modelOther.has(provider)}
              onSelectKnown={(modelId) => {
                setModelByProvider((prev) => ({ ...prev, [provider]: modelId }))
                setModelOther((prev) => {
                  const next = new Set(prev)
                  next.delete(provider)
                  return next
                })
              }}
              onSelectOther={() => {
                setModelOther((prev) => new Set(prev).add(provider))
              }}
              onCustomTextChange={(text) => {
                setModelByProvider((prev) => ({ ...prev, [provider]: text }))
              }}
              disabled={isSubmitting}
            />
          ))}
        </div>
      </Section>

      <Section
        title="Horário humano"
        description="Os dias/faixas em que uma PESSOA atende; fora deles o robô responde. Nenhum dia marcado = o robô sempre pode responder."
      >
        <WorkingHoursEditor
          value={workingHours}
          onChange={(next) => {
            setWorkingHours(next ?? DEFAULT_WORKING_HOURS)
          }}
        />
      </Section>

      <Section title="Handoff e tetos">
        <Field
          label="Palavras de handoff"
          hint="Se o cliente escrever uma delas, a conversa volta para a fila humana."
          help={({ id }) => (
            <HelpHint label="palavras de handoff" id={id}>
              Se a mensagem do cliente CONTIVER uma destas palavras (sem
              diferenciar maiúsculas — é assim que se escreve no WhatsApp), o
              robô para de responder e devolve a conversa para a fila. É a saída
              de emergência para quem não quer falar com o robô.
            </HelpHint>
          )}
        >
          {(field) => (
            <TagInput
              {...field}
              value={handoffKeywords}
              onValueChange={setHandoffKeywords}
              placeholder="atendente, humano, pessoa…"
            />
          )}
        </Field>

        <Field
          label="Máximo de respostas por sessão"
          error={errors.maxRepliesPerSession?.message}
        >
          {(field) => (
            <Input
              {...field}
              type="number"
              min={1}
              {...register('maxRepliesPerSession', { valueAsNumber: true })}
            />
          )}
        </Field>

        <Field
          label="Teto de custo por sessão (USD)"
          error={
            maxCostInvalid ? 'Informe um valor maior que zero.' : undefined
          }
          hint="Limite duro — ao atingi-lo, o robô encerra a sessão e devolve a conversa para um humano."
          help={({ id }) => (
            <HelpHint label="teto de custo por sessão" id={id}>
              Por SESSÃO do robô (o intervalo entre um handoff e o próximo), não
              pela conversa inteira. Cada chamada ao provedor de IA é dinheiro
              de verdade — ao atingir o teto, o robô para e devolve a conversa
              para um humano, em vez de continuar gastando.
            </HelpHint>
          )}
        >
          {(field) => (
            <MoneyInput
              {...field}
              value={maxCostWire}
              onValueChange={setMaxCostWire}
              scale={2}
              // O `R$` embutido do componente é para reais — este teto é em
              // dólar (a moeda em que os provedores de IA cobram), então o
              // símbolo fica desligado e a moeda mora só no rótulo do campo.
              symbol={false}
            />
          )}
        </Field>
      </Section>

      {formError === null ? null : (
        <p role="alert" className="text-(length:--text-sm) text-(--danger)">
          {formError}
        </p>
      )}

      <div>
        <Button
          type="submit"
          variant="primary"
          loading={isSubmitting || save.isPending}
          disabled={maxCostInvalid}
        >
          Salvar configuração
        </Button>
      </div>
    </form>
  )
}

const CredentialDialog = ({
  provider,
  open,
  onOpenChange,
}: {
  provider: string
  open: boolean
  onOpenChange: (open: boolean) => void
}): ReactNode => {
  const queryClient = useQueryClient()
  const [apiKey, setApiKey] = useState('')
  const [baseUrl, setBaseUrl] = useState('')
  const [formError, setFormError] = useState<string | null>(null)

  const save = useMutation({
    mutationFn: async () => {
      await saveLlmCredential({
        provider: provider as (typeof PROVIDERS)[number],
        apiKey,
        baseUrl: baseUrl.trim() === '' ? null : baseUrl.trim(),
      })
    },
    onSuccess: async () => {
      setFormError(null)
      setApiKey('')
      setBaseUrl('')
      await queryClient.invalidateQueries({ queryKey: ['llm-credentials'] })
      onOpenChange(false)
    },
    onError: (cause: unknown) => {
      setFormError(
        cause instanceof ApiError
          ? cause.message
          : 'Não foi possível salvar esta chave.'
      )
    },
  })

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        title={`Chave — ${PROVIDER_LABELS[provider] ?? provider}`}
        description="A chave nunca volta a aparecer depois de salva — só se ela existe."
        className="max-w-md"
      >
        <form
          noValidate
          className="flex flex-col gap-4"
          onSubmit={(event) => {
            event.preventDefault()
            setFormError(null)
            save.mutate()
          }}
        >
          <Field label="Chave de API" required>
            {(field) => (
              <Input
                {...field}
                type="password"
                value={apiKey}
                onChange={(event) => {
                  setApiKey(event.target.value)
                }}
                autoComplete="off"
              />
            )}
          </Field>

          {provider === 'openrouter' ? (
            <Field
              label="URL base"
              hint="Opcional — endereço alternativo compatível."
            >
              {(field) => (
                <Input
                  {...field}
                  value={baseUrl}
                  onChange={(event) => {
                    setBaseUrl(event.target.value)
                  }}
                />
              )}
            </Field>
          ) : null}

          {formError === null ? null : (
            <p role="alert" className="text-(length:--text-sm) text-(--danger)">
              {formError}
            </p>
          )}

          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="ghost">
                Cancelar
              </Button>
            </DialogClose>
            <Button type="submit" variant="primary" loading={save.isPending}>
              Salvar
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

const CredentialsSection = ({
  credentials,
  canManage,
}: {
  credentials: readonly LlmCredentialSummaryType[]
  canManage: boolean
}): ReactNode => {
  const [editing, setEditing] = useState<string | null>(null)

  const byProvider = new Map(credentials.map((c) => [c.provider, c]))

  return (
    <TableContainer>
      <Table>
        <TableHead>
          <TableRow>
            <TableHeaderCell>Provedor</TableHeaderCell>
            <TableHeaderCell>Chave</TableHeaderCell>
            <TableHeaderCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {PROVIDERS.map((provider) => {
            const credential = byProvider.get(provider)
            return (
              <TableRow key={provider}>
                <TableCell>{PROVIDER_LABELS[provider]}</TableCell>
                <TableCell>
                  {credential?.hasApiKey === true ? (
                    <Badge
                      tone="success"
                      icon={
                        <CircleCheck aria-hidden="true" className="h-3 w-3" />
                      }
                    >
                      Cadastrada
                    </Badge>
                  ) : (
                    <Badge
                      tone="neutral"
                      icon={
                        <CircleDashed aria-hidden="true" className="h-3 w-3" />
                      }
                    >
                      Não cadastrada
                    </Badge>
                  )}
                </TableCell>
                <TableCell>
                  {canManage ? (
                    <Button
                      variant="ghost"
                      onClick={() => {
                        setEditing(provider)
                      }}
                    >
                      <KeyRound aria-hidden="true" className="h-4 w-4" />
                      {credential?.hasApiKey === true ? 'Trocar' : 'Cadastrar'}
                    </Button>
                  ) : null}
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
      {editing === null ? null : (
        <CredentialDialog
          provider={editing}
          open
          onOpenChange={(open) => {
            if (!open) setEditing(null)
          }}
        />
      )}
    </TableContainer>
  )
}

export const AtendimentoRoboPage = (): ReactNode => {
  const { allows } = useAuth()
  const canManage = allows('communication.bot.manage')

  const configs = useQuery({
    queryKey: ['bot-configs'],
    queryFn: async ({ signal }) => await fetchBotConfigs(signal),
  })
  const credentials = useQuery({
    queryKey: ['llm-credentials'],
    queryFn: async ({ signal }) => await fetchLlmCredentials(signal),
  })
  const sessions = useQuery({
    queryKey: ['bot-sessions'],
    queryFn: async ({ signal }) => await fetchBotSessions(signal),
  })

  return (
    <>
      <TabHeader
        title="Robô de atendimento"
        description="O robô que responde primeiro, dentro da janela de 24h — e sabe a hora de chamar gente."
      />

      <Tabs defaultValue="configuracao">
        <TabsList>
          <TabsTrigger value="configuracao">Configuração</TabsTrigger>
          <TabsTrigger value="provedores">Provedores</TabsTrigger>
          <TabsTrigger value="gasto">Gasto</TabsTrigger>
        </TabsList>

        <TabsContent value="configuracao" className="mt-4">
          {configs.isPending ? (
            <div className="flex justify-center py-8">
              <Spinner />
            </div>
          ) : configs.isError ? (
            <EmptyState
              icon={
                <MessageSquareWarning aria-hidden="true" className="h-8 w-8" />
              }
              title="Não foi possível carregar a configuração do robô"
              description="Verifique a conexão com o servidor e tente novamente."
            />
          ) : canManage ? (
            <ConfigForm config={tenantConfig(configs.data)} />
          ) : (
            <p className="text-(length:--text-sm) text-(--text-muted)">
              Só owner, admin e manager podem alterar esta configuração.
            </p>
          )}
        </TabsContent>

        <TabsContent value="provedores" className="mt-4">
          {credentials.isPending ? (
            <div className="flex justify-center py-8">
              <Spinner />
            </div>
          ) : credentials.isError ? (
            <EmptyState
              icon={<KeyRound aria-hidden="true" className="h-8 w-8" />}
              title="Não foi possível carregar os provedores"
              description="Verifique a conexão com o servidor e tente novamente."
            />
          ) : (
            <CredentialsSection
              credentials={credentials.data}
              canManage={canManage}
            />
          )}
        </TabsContent>

        <TabsContent value="gasto" className="mt-4">
          {sessions.isPending ? (
            <div className="flex justify-center py-8">
              <Spinner />
            </div>
          ) : sessions.isError ? (
            <EmptyState
              icon={<Bot aria-hidden="true" className="h-8 w-8" />}
              title="Não foi possível carregar o gasto do período"
              description="Verifique a conexão com o servidor e tente novamente."
            />
          ) : (
            <div className="flex flex-col gap-4">
              <p className="text-(length:--text-sm) text-(--text-muted)">
                Total nos últimos 30 dias: US${' '}
                {(Number(sessions.data.totalCostMicrosUsd) / 1_000_000).toFixed(
                  4
                )}
              </p>

              {sessions.data.sessions.length === 0 ? (
                <EmptyState
                  icon={<Bot aria-hidden="true" className="h-8 w-8" />}
                  title="Nenhuma sessão do robô ainda"
                  description="Quando o robô responder uma conversa, ela aparece aqui com o custo e o motivo do encerramento."
                />
              ) : (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableHeaderCell>Conversa</TableHeaderCell>
                        <TableHeaderCell>Provedor</TableHeaderCell>
                        <TableHeaderCell>Modelo</TableHeaderCell>
                        <TableHeaderCell>Respostas</TableHeaderCell>
                        <TableHeaderCell>Custo (USD)</TableHeaderCell>
                        <TableHeaderCell>Encerrada por</TableHeaderCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {sessions.data.sessions.map((session) => (
                        <TableRow key={session.id}>
                          <TableCell>
                            {session.contactLabel ?? '(contato removido)'}
                          </TableCell>
                          <TableCell>
                            {session.providerUsed === null
                              ? '—'
                              : (PROVIDER_LABELS[session.providerUsed] ??
                                session.providerUsed)}
                          </TableCell>
                          <TableCell>{session.modelUsed ?? '—'}</TableCell>
                          <TableCell>{session.replyCount}</TableCell>
                          <TableCell>
                            {(
                              Number(session.costMicrosUsd) / 1_000_000
                            ).toFixed(4)}
                          </TableCell>
                          <TableCell>
                            {session.endReason === null ? (
                              <Badge
                                tone="info"
                                icon={
                                  <Bot aria-hidden="true" className="h-3 w-3" />
                                }
                              >
                                Em andamento
                              </Badge>
                            ) : (
                              (END_REASON_LABELS[session.endReason] ??
                              session.endReason)
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </>
  )
}
